import { httpClient } from '../client';
import {
  Organisation,
  OrganisationFormData,
  OrganisationResponse,
  requestOrganisation,
} from '../../../types/organization';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';
import axios from 'axios';

class OrganisationEndpoints extends BaseEndpoint {
  private static instance: OrganisationEndpoints;

  private constructor() {
    const baseURL = import.meta.env.VITE_API_BASE_URL;
    super(baseURL);
  }

  static getInstance(): OrganisationEndpoints {
    if (!OrganisationEndpoints.instance) {
      OrganisationEndpoints.instance = new OrganisationEndpoints();
    }
    return OrganisationEndpoints.instance;
  }

  async createOrganisation(data: OrganisationFormData): Promise<Organisation> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<{ data: Organisation }>(`/organisations`, data, {
        headers,
      });

      logger.info('Organisation created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create organisation:', error);
      throw error;
    }
  }

  async getSingleOrganisation(orgId: string): Promise<Organisation> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<OrganisationResponse>(`/organisations/${orgId}`, {
        headers,
      });
      logger.info('Organisation fetched successfully', response);
      return response.data.items?.[0] ?? null;
    } catch (error) {
      logger.error('Failed to fetch organization:', error);
      throw error;
    }
  }

  async updateOrganisation(
    organizationId: string,
    data: Partial<Organisation>
  ): Promise<Organisation> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<{ data: Organisation }>(
        `/updateclient/${organizationId}`,
        data,
        { headers }
      );

      logger.info('Organisation updated successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update organization:', error);
      throw error;
    }
  }

  async deleteOrganisation(id: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`/removeclient/${id}`, {
        headers,
      });

      logger.info('Organisation deleted successfully');
    } catch (error) {
      logger.error('Failed to delete organization:', error);
      throw error;
    }
  }

  async requestOrganisation(
    data: requestOrganisation
  ): Promise<{ success: boolean; checkoutUrl?: string; isTrial?: boolean; id?: string }> {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_PUBLIC_API_BASE_URL}/organisations/request`,
        data
      );

      if (response.status === 200) {
        // Return organization data including ID for OAuth users
        return {
          success: true,
          checkoutUrl: response.data.checkoutUrl,
          isTrial: response.data.isTrial || false,
          id: response.data.id || response.data.clientId || response.data.organisationId,
        };
      }

      throw new Error(`Unexpected response status: ${response.status}`);
    } catch (error) {
      logger.error('Failed to request organization:', error);
      throw new Error(
        `Organisation request failed: ${axios.isAxiosError(error) && error.response ? error.response.data.message : 'Unknown error'}`
      );
    }
  }

  async getActiveteOrganisations(id: string): Promise<Organisation[]> {
    try {
      const response = await axios.get<OrganisationResponse>(
        `${import.meta.env.VITE_PUBLIC_API_BASE_URL}/organisations/${id}`
      );
      return response.data.items;
    } catch (error) {
      logger.error('Failed to fetch organizations:', error);
      throw error;
    }
  }

  /**
   * Get organization ingest status from public API
   * Used to check if file/URL ingestion is in progress
   * Reuses getActiveteOrganisations to avoid code duplication
   */
  async getOrganisationIngestStatus(
    clientId: string
  ): Promise<{ ingestStatus: string | null; organisation: Organisation | null }> {
    try {
      const orgs = await this.getActiveteOrganisations(clientId);
      const org = orgs?.[0] ?? null;
      // Check both snake_case and camelCase versions of the field
      const status = org?.ingestStatus ?? org?.ingest_status ?? null;
      logger.info('Organization ingest status check:', { clientId, status, org });
      return {
        ingestStatus: status,
        organisation: org,
      };
    } catch (error) {
      logger.error('Failed to fetch organization ingest status:', error);
      throw error;
    }
  }

  async invokeOrganisationCreation(id: string) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post(
        `organisation/create/${id}`,
        { clientId: id },
        {
          headers,
        }
      );

      return response.data.data;
    } catch (error) {
      logger.error('Failed to Invoke organizations:', error);
      throw error;
    }
  }
}

export const organizationEndpoints = OrganisationEndpoints.getInstance();
